import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { BudgetService } from '@core/services/budget.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import { Budget, BudgetDetail, BudgetCreate, BudgetUpdate } from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';

@Injectable({ providedIn: 'root' })
export class BudgetQuery {
  private readonly budgetService = inject(BudgetService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  useBudgetsGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();
      return {
        queryKey: [...QUERY_KEYS.budgets.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.budgetService.getBudgets(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Budget>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useBudgetDetail(budgetId: () => string | null) {
    return injectQuery(() => {
      const id = budgetId();
      return {
        queryKey: QUERY_KEYS.budgets.detail(id || ''),
        queryFn: () => lastValueFrom(this.budgetService.getBudget(id!)),
        select: (data: ApiResponse<BudgetDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useCreateBudget() {
    return injectMutation(() => ({
      mutationFn: (data: BudgetCreate) =>
        lastValueFrom(this.budgetService.createBudget(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.all });
      },
    }));
  }

  useUpdateBudget() {
    return injectMutation(() => ({
      mutationFn: (data: BudgetUpdate) =>
        lastValueFrom(this.budgetService.updateBudget(data)),
      onSuccess: (_result, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.all });
      },
    }));
  }

  useDeleteBudget() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.budgetService.deleteBudget(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.all });
      },
    }));
  }

  useApproveBudget() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.budgetService.approveBudget(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.budgets.all });
      },
    }));
  }
}
